import { useIntl } from "react-intl";
import { ErrorsType } from "..";
import { Custom } from "./Custom";

interface Props {
    tasOccurrenceNumber?: string;
    isLoading: boolean;
    subject?: string;
    customerComment: string;
    handleChangeComment: (comment: string) => void;
    errors: ErrorsType;
    partOrComponentName: string;
    handlePartOrComponentName: (value: string) => void;
    partNumber: string;
    handlePartNumber: (value: string) => void;
    problemDescription: string;
    handleProblemDescription: (value: string) => void;
    diagnosticAndResults: string;
    handleDiagnosticAndResults: (value: string) => void;
    problemSteps: string;
    handleProblemSteps: (value: string) => void;
    failureFrequency: string;
    handleFailureFrequency: (value: string) => void;
    failCodes: string;
    handleFailCodes: (value: string) => void;
    repair: string;
    handleRepair: (value: string) => void;
    canEditRepair: boolean;
    observations: string;
    handleObservations: (value: string) => void;
}

const AdditionalDataTas = ({
    tasOccurrenceNumber,
    isLoading,
    customerComment,
    handleChangeComment,
    subject,
    errors,
    partOrComponentName,
    handlePartOrComponentName,
    partNumber,
    handlePartNumber,
    problemDescription,
    handleProblemDescription,
    diagnosticAndResults,
    handleDiagnosticAndResults,
    problemSteps,
    handleProblemSteps,
    failureFrequency,
    handleFailureFrequency,
    failCodes,
    handleFailCodes,
    repair,
    handleRepair,
    canEditRepair,
    observations,
    handleObservations
}: Props) => {
    const intl = useIntl();
    const optionalPlaceholder = intl.formatMessage({ id: "intl-msg:occurrence.optional" });
    const requiredPlaceholder = intl.formatMessage({ id: "intl-msg:occurrence.required" });

    const supportTypesWithProblemSteps = [
        'GEARBOX',
        'CHASSIS',
        'SUPPORT_AXLE',
        'FRONT_AXLE',
        'CLUTCH',
        'ENGINE',
        'E_DELIVERY',
        'WHEEL',
        'STEERING_SYSTEM',
        'BRAKE_SYSTEM',
        'ELECTRICAL_SYSTEM',
        'DRIVE_AXLE',
        'SUSPENSION',
        'POWER_TAKEOFF',
        'TRANSMISSION',
    ];

    const shouldShowProblemSteps = supportTypesWithProblemSteps.includes(subject as string);

    return (
        <div className="width-100pct height-100pct border rounded padding-15">
            <div className="margin-bottom-15">
                <span className="text-size-14 text-medium text-color-black">{intl.formatMessage({ id: "intl-msg:support.administrative.additionalDataTitle" })}</span>
            </div>
            <div className="width-100pct display-flex flex-row gap-20">
                {/* <Custom.Input label='Número do contrato*' value={tasOccurrenceNumber} disabled={true} isLoading={isLoading} /> */}

                <Custom.Input
                    label={intl.formatMessage({ id: "intl-msg:support.constants.field.partOrComponentName" }) + "*"}
                    value={partOrComponentName}
                    isLoading={isLoading}
                    onChange={handlePartOrComponentName}
                    error={errors.partOrComponentName}
                    placeholder={requiredPlaceholder}
                />
                <Custom.Input
                    label={intl.formatMessage({ id: "intl-msg:support.constants.field.partNumber" }) + "*"}
                    value={partNumber}
                    isLoading={isLoading}
                    onChange={handlePartNumber}
                    error={errors.partNumber}
                    placeholder={requiredPlaceholder}
                />
            </div>


            {subject === 'ENGINE_REPLACEMENT_REQUEST' || subject === 'TAS_DIAGNOSIS_TOOLS' ? null : (
                <div className="width-100pct">
                    <Custom.TextArea
                        label={intl.formatMessage({ id: "intl-msg:support.tasAdditionalRequiredFields.customerComplaint" }) + "*"}
                        value={customerComment}
                        onChange={handleChangeComment}
                        isLoading={isLoading}
                        error={errors.customerComment}
                        placeholder={requiredPlaceholder}
                    />
                </div>
            )}

            {subject === 'ENGINE' ? (
                <Custom.TextArea
                    label={intl.formatMessage({ id: "intl-msg:support.constants.field.failureFrequency" })}
                    value={failureFrequency}
                    onChange={handleFailureFrequency}
                    isLoading={isLoading}
                    // error={errors.failureFrequency}
                    placeholder={optionalPlaceholder}
                />
            ) : null}

            {subject === 'CABIN' || subject === 'ENGINE_REPLACEMENT_REQUEST' || subject === 'TAS_DIAGNOSIS_TOOLS' ? (
                <div className="width-100pct">
                    <Custom.TextArea
                        label={intl.formatMessage({ id: "intl-msg:support.constants.field.problemDescription" })}
                        value={problemDescription}
                        onChange={handleProblemDescription}
                        isLoading={isLoading}
                        error={errors.problemDescription}
                        placeholder={optionalPlaceholder}
                    />
                </div>
            ) : null}

            {shouldShowProblemSteps ? (
                <div className="width-100pct">
                    <Custom.TextArea
                        label={intl.formatMessage({ id: "intl-msg:support.constants.field.problemSteps" }) + "*"}
                        value={problemSteps}
                        onChange={handleProblemSteps}
                        isLoading={isLoading}
                        error={errors.problemSteps}
                        placeholder={requiredPlaceholder}
                    />
                </div>
            ) : null}

            {subject === 'SOFTWARE_IDLE_AUTO_SHUTDOWN' ? null : (
                <div className="width-100pct">
                    <Custom.TextArea
                        label={intl.formatMessage({ id: "intl-msg:support.constants.field.diagnosticAndResults" }) + "*"}
                        value={diagnosticAndResults}
                        onChange={handleDiagnosticAndResults}
                        isLoading={isLoading}
                        error={errors.diagnosticAndResults}
                        placeholder={requiredPlaceholder}
                    />
                </div>
            )}

            <div className="width-100pct">
                <Custom.TextArea
                    label={intl.formatMessage({ id: "intl-msg:support.constants.field.relatedFaultCodes" })}
                    value={failCodes}
                    onChange={handleFailCodes}
                    isLoading={isLoading}
                    placeholder={optionalPlaceholder}
                />
            </div>

            <div className="width-100pct">
                <Custom.TextArea
                    label={intl.formatMessage({ id: "intl-msg:support.constants.field.repairSolution" })}
                    value={repair}
                    onChange={handleRepair}
                    isLoading={isLoading}
                    disabled={!canEditRepair}
                    placeholder={canEditRepair ? optionalPlaceholder : undefined}
                />
            </div>

            <div className="width-100pct">
                <Custom.TextArea
                    label={intl.formatMessage({ id: "intl-msg:support.constants.field.observations" })}
                    value={observations}
                    onChange={handleObservations}
                    isLoading={isLoading}
                    placeholder={optionalPlaceholder}
                />
            </div>
        </div >
    )
}

export default AdditionalDataTas;